import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftEndOnRectangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  DocumentArrowUpIcon,
  FunnelIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import supabase from './utils/supabaseClient';
import { isValidEmail } from './utils/validators';
import AdminPedidosTab from './AdminPedidosTab';
import AdminFinanceiroTab from './AdminFinanceiroTab';
import AdminLgpdTab from './AdminLgpdTab';
import AdminLeadsTab from './AdminLeadsTab';

const STATUS_META = {
  pendente:   { label: 'Aguardando',  bg: 'bg-accent-100',  text: 'text-accent-700'  },
  em_analise: { label: 'Em análise',  bg: 'bg-brand-100',   text: 'text-brand-700'   },
  aprovado:   { label: 'Aprovado',    bg: 'bg-green-100',   text: 'text-green-700'   },
  rejeitado:  { label: 'Reprovado',   bg: 'bg-red-100',     text: 'text-red-700'     },
  inativo:    { label: 'Inativo',     bg: 'bg-ink-100',     text: 'text-ink-500'     },
};

const STATUS_OPTIONS = [
  { value: 'pendente',   label: 'Aguardando análise' },
  { value: 'em_analise', label: 'Em análise' },
  { value: 'aprovado',   label: 'Aprovado' },
  { value: 'rejeitado',  label: 'Reprovado' },
  { value: 'inativo',    label: 'Inativo' },
];

const STATUS_FILTERS = [
  { value: '',          label: 'Todos' },
  { value: 'pendente',  label: 'Pendentes' },
  { value: 'em_analise',label: 'Em análise' },
  { value: 'aprovado',  label: 'Aprovados' },
  { value: 'rejeitado', label: 'Reprovados' },
];

const VEHICLE_LABELS = {
  motorcycle:  'Moto',
  car:         'Carro',
  smallPickup: 'Picape pequena',
  van:         'Van / Furgão',
  truck:       'Camionete',
  bigTruck:    'Caminhão',
};

const DOCS = [
  { field: 'cnh_url',    label: 'CNH' },
  { field: 'crlv_url',   label: 'CRLV' },
  { field: 'selfie_url', label: 'Selfie com CNH' },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] ?? STATUS_META.pendente;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
};

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">{title}</p>
    <div className="rounded-xl border border-ink-100 overflow-hidden">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2 px-4 py-2.5 border-b border-ink-50 last:border-0 text-sm">
    <span className="text-ink-500 shrink-0">{label}</span>
    <span className="text-ink-900 font-medium text-right break-words">{value || '—'}</span>
  </div>
);

const AdminPanel = () => {
  const [activeTab, setActiveTab]   = useState('pedidos');
  const [authState, setAuthState]   = useState('loading');
  const [session, setSession]       = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [editStatus, setEditStatus] = useState('pendente');
  const [editNotes, setEditNotes]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [email, setEmail]           = useState('');
  const [linkSent, setLinkSent]     = useState(false);
  const [sending, setSending]       = useState(false);

  // Auth + admin check
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s) {
        const { data } = await supabase.rpc('is_admin');
        setAuthState(data ? 'admin' : 'not_admin');
      } else {
        setAuthState('unauthenticated');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      if (s) {
        const { data } = await supabase.rpc('is_admin');
        setAuthState(data ? 'admin' : 'not_admin');
      } else {
        setAuthState('unauthenticated');
        setApplications([]);
        setSelected(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load data
  useEffect(() => {
    if (authState !== 'admin') return;
    setLoading(true);
    supabase
      .from('entregadores')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        setLoading(false);
        if (error) { toast.error('Erro ao carregar inscrições.'); return; }
        setApplications(data ?? []);
      });
  }, [authState, refreshKey]);

  // ESC closes panel
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') setSelected(null); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, []);

  // Sync edit fields when selection changes
  const selectedId = selected?.id;
  useEffect(() => {
    if (selected) {
      setEditStatus(selected.status || 'pendente');
      setEditNotes(selected.notas_internas || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const stats = useMemo(() => {
    const c = { total: applications.length, pendente: 0, em_analise: 0, aprovado: 0, rejeitado: 0 };
    applications.forEach((a) => { if (c[a.status] !== undefined) c[a.status]++; });
    return c;
  }, [applications]);

  const filtered = useMemo(() => {
    let list = applications;
    if (statusFilter) list = list.filter((a) => a.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((a) =>
        [a.primeiro_nome, a.sobrenome, a.email, a.cpf, a.whatsapp]
          .some((v) => v && v.toLowerCase().includes(s))
      );
    }
    return list;
  }, [applications, statusFilter, search]);

  const handleSignOut = () => supabase.auth.signOut();

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { toast.error('Informe um e-mail válido.'); return; }
    setSending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + '/admin' },
    });
    setSending(false);
    if (error) { toast.error('Erro ao enviar link.'); return; }
    setLinkSent(true);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    const { error } = await supabase
      .from('entregadores')
      .update({ status: editStatus, notas_internas: editNotes })
      .eq('id', selected.id);
    setSaving(false);
    if (error) { toast.error('Erro ao salvar.'); return; }
    const updated = { ...selected, status: editStatus, notas_internas: editNotes };
    setApplications((apps) => apps.map((a) => (a.id === selected.id ? updated : a)));
    setSelected(updated);
    toast.success('Salvo com sucesso.');
  };

  const openDoc = async (path) => {
    if (!path) return;
    const { data, error } = await supabase.storage
      .from('driver-documents')
      .createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) { toast.error('Erro ao abrir documento.'); return; }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  // ─── Loading ───
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
      </div>
    );
  }

  // ─── Unauthenticated ───
  if (authState === 'unauthenticated') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <ToastContainer position="top-right" theme="colored" />
        <div className="w-full max-w-sm card p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/fretz-logo-light.png" alt="Fretz" className="h-9 w-auto" />
            <span className="text-base font-normal text-ink-500 border-l border-ink-200 pl-3">admin</span>
          </div>

          {linkSent ? (
            <div className="text-center">
              <EnvelopeIcon className="h-10 w-10 text-brand-600 mx-auto mb-3" />
              <p className="font-semibold text-ink-900">Verifique seu e-mail</p>
              <p className="text-sm text-ink-500 mt-1">
                Enviamos um link para <strong className="text-ink-900">{email}</strong>.
              </p>
              <button
                onClick={() => setLinkSent(false)}
                className="mt-5 text-sm text-brand-700 font-semibold hover:underline"
              >
                Usar outro e-mail
              </button>
            </div>
          ) : (
            <form onSubmit={handleSendLink} className="space-y-4" noValidate>
              <div>
                <label className="label" htmlFor="admin-email">E-mail do administrador</label>
                <input
                  id="admin-email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@fretz.com.br"
                  autoFocus
                  autoComplete="email"
                />
              </div>
              <button type="submit" disabled={sending} className="btn-primary w-full gap-2">
                {sending ? 'Enviando…' : (
                  <>Receber link de acesso <ArrowRightIcon className="h-4 w-4" /></>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ─── Not admin ───
  if (authState === 'not_admin') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <ToastContainer position="top-right" theme="colored" />
        <div className="w-full max-w-sm card p-8 text-center">
          <XMarkIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="font-display font-bold text-ink-900 text-lg">Acesso negado</p>
          <p className="text-sm text-ink-500 mt-2">
            <strong className="text-ink-900">{session?.user?.email}</strong> não tem acesso ao painel.
          </p>
          <button onClick={handleSignOut} className="mt-5 btn-secondary text-sm gap-2">
            <ArrowLeftEndOnRectangleIcon className="h-4 w-4" /> Sair
          </button>
        </div>
      </div>
    );
  }

  // ─── Admin dashboard ───
  return (
    <div className="min-h-screen bg-ink-50">
      <ToastContainer position="top-right" theme="colored" />

      {/* Admin header */}
      <header className="sticky top-0 z-40 bg-white border-b border-ink-100 shadow-soft">
        <div className="container mx-auto px-4 lg:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" aria-label="Fretz - Home">
              <img src="/fretz-logo-light.png" alt="Fretz" className="h-8 w-auto" />
            </Link>
            <span className="text-ink-200 select-none">|</span>
            <span className="font-semibold text-sm text-ink-600">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="btn-ghost text-sm gap-1.5"
              title="Atualizar lista"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </button>
            <button onClick={handleSignOut} className="btn-ghost text-sm gap-1.5">
              <ArrowLeftEndOnRectangleIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-ink-100">
        <div className="container mx-auto px-4 lg:px-6 max-w-6xl flex gap-1">
          {[
            { id: 'pedidos',      label: 'Pedidos' },
            { id: 'financeiro',   label: 'Financeiro' },
            { id: 'entregadores', label: 'Entregadores' },
            { id: 'leads',        label: 'Leads' },
            { id: 'lgpd',         label: 'LGPD' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition
                ${activeTab === t.id
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-ink-500 hover:text-ink-900'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 py-8 max-w-6xl">

      {activeTab === 'pedidos' && <AdminPedidosTab />}

      {activeTab === 'financeiro' && <AdminFinanceiroTab />}

      {activeTab === 'lgpd' && <AdminLgpdTab />}

      {activeTab === 'leads' && <AdminLeadsTab />}

      {activeTab === 'entregadores' && (<>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total',      value: stats.total,      color: 'text-ink-900'   },
            { label: 'Pendentes',  value: stats.pendente,   color: 'text-accent-600'},
            { label: 'Em análise', value: stats.em_analise, color: 'text-brand-700' },
            { label: 'Aprovados',  value: stats.aprovado,   color: 'text-green-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="card p-5">
              <p className="text-sm text-ink-500">{label}</p>
              <p className={`text-3xl font-display font-bold mt-1 ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="card p-4 mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <FunnelIcon className="h-4 w-4 text-ink-400 shrink-0" />
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition
                  ${statusFilter === f.value
                    ? 'bg-brand-gradient text-white shadow-glow'
                    : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto w-full sm:w-64">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-9 text-sm"
              type="search"
              placeholder="Nome, e-mail, CPF…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          {loading && applications.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-ink-400">
              <UserIcon className="h-10 w-10 mx-auto mb-3" />
              <p className="text-sm font-medium">Nenhuma inscrição encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ink-100 bg-ink-50/70">
                    <th className="text-left px-4 py-3 font-semibold text-ink-500">Nome</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500 hidden md:table-cell">E-mail</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500 hidden sm:table-cell">Veículo</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500 hidden lg:table-cell">Cadastro</th>
                    <th className="w-8 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((app) => (
                    <tr
                      key={app.id}
                      onClick={() => setSelected(app)}
                      className="border-b border-ink-50 hover:bg-brand-50/40 cursor-pointer transition"
                    >
                      <td className="px-4 py-3 font-medium text-ink-900">
                        {[app.primeiro_nome, app.sobrenome].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-ink-500 hidden md:table-cell">{app.email || '—'}</td>
                      <td className="px-4 py-3 text-ink-500 hidden sm:table-cell">
                        {VEHICLE_LABELS[app.tipo_veiculo] || app.tipo_veiculo || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={app.status} />
                      </td>
                      <td className="px-4 py-3 text-ink-400 hidden lg:table-cell">{fmt(app.created_at)}</td>
                      <td className="px-2 py-3 text-ink-300">
                        <ChevronRightIcon className="h-4 w-4" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-ink-400 text-right px-4 py-3 border-t border-ink-50">
                {filtered.length} inscrição{filtered.length !== 1 ? 'ões' : ''}
                {statusFilter || search ? ` (de ${applications.length} total)` : ''}
              </p>
            </div>
          )}
        </div>
      </>)}
      </div>

      {/* Detail slide-over (entregadores) */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-ink-900/40 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <aside className="w-full max-w-lg bg-white shadow-xl flex flex-col overflow-hidden animate-[fade-up_0.2s_ease]">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-ink-100 shrink-0 gap-3">
              <div className="min-w-0">
                <p className="font-display font-bold text-ink-900 truncate">
                  {[selected.primeiro_nome, selected.sobrenome].filter(Boolean).join(' ') || 'Entregador'}
                </p>
                <p className="text-xs text-ink-500 truncate mt-0.5">{selected.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={selected.status} />
                <button
                  onClick={() => setSelected(null)}
                  className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <Section title="Dados pessoais">
                <Row label="Nome" value={[selected.primeiro_nome, selected.sobrenome].filter(Boolean).join(' ')} />
                <Row label="CPF" value={selected.cpf} />
                <Row label="Nascimento" value={fmt(selected.data_nascimento)} />
              </Section>

              <Section title="Contato">
                <Row label="E-mail" value={selected.email} />
                <Row label="WhatsApp" value={selected.whatsapp} />
                <Row label="Localidade" value={[selected.bairro, selected.cidade].filter(Boolean).join(', ')} />
              </Section>

              <Section title="Veículo">
                <Row label="Tipo" value={VEHICLE_LABELS[selected.tipo_veiculo] || selected.tipo_veiculo} />
                <Row label="CNH" value={selected.cnh_categoria ? `Categoria ${selected.cnh_categoria}` : null} />
                <Row label="Placa" value={selected.veiculo_placa} />
                <Row label="Modelo / Ano" value={[selected.veiculo_modelo, selected.veiculo_ano].filter(Boolean).join(' ')} />
              </Section>

              <Section title="Documentos">
                {DOCS.map(({ field, label }) => (
                  <div key={field} className="flex items-center justify-between px-4 py-2.5 border-b border-ink-50 last:border-0">
                    <div className="flex items-center gap-2 text-sm">
                      <DocumentArrowUpIcon className={`h-4 w-4 ${selected[field] ? 'text-brand-600' : 'text-ink-300'}`} />
                      <span className={selected[field] ? 'text-ink-900' : 'text-ink-400'}>{label}</span>
                    </div>
                    {selected[field] ? (
                      <button
                        type="button"
                        onClick={() => openDoc(selected[field])}
                        className="text-xs font-semibold text-brand-700 hover:underline"
                      >
                        Abrir
                      </button>
                    ) : (
                      <span className="text-xs text-ink-400">Não enviado</span>
                    )}
                  </div>
                ))}
              </Section>

              <Section title="Atualizar inscrição">
                <div className="p-4 space-y-3">
                  <div>
                    <label className="label" htmlFor="edit-status">Status</label>
                    <select
                      id="edit-status"
                      className="input"
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label" htmlFor="edit-notes">Notas internas</label>
                    <textarea
                      id="edit-notes"
                      className="input min-h-[90px] resize-y"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Observações internas (não visíveis ao entregador)…"
                    />
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-primary w-full"
                  >
                    {saving ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                </div>
              </Section>

              <p className="text-xs text-ink-400 text-right pb-2">
                Cadastro em {fmt(selected.created_at)}
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
